// src/admin/pages/RolesPage.jsx
//
// Role catalog admin page. Route: /roles.
//
// Same structure as admin/pages/PermissionsPage.jsx and
// accounts/pages/AccountsPage.jsx: PageHeader with the action on the right, a
// StatTile summary row, one DataTable, create/edit behind a modal. Replaces
// pages/RolesPage.jsx + pages/CreateRoleForm.jsx (a hand-rolled <table>, an
// always-open inline form with 38 bare checkboxes, window.confirm, and
// styles/RolesPage.css) — all deleted with this change.
//
// The permission catalog is fetched here rather than inside the modal so
// opening the form is instant and a catalog failure is visible before you
// start filling anything in.

import { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, Plus, KeyRound, Layers } from "lucide-react";
import {
  PageHeader,
  Button,
  EmptyState,
  DataTable,
  StatusBadge,
  StatTile,
  Alert,
  ConfirmModal,
  SkeletonTable,
} from "../../shared/ui";
import { listRoles, createRole, updateRole, deactivateRole } from "../api/rolesApi";
import { listPermissions } from "../api/permissionsApi";
import { useAuth } from "../../context/AuthContext";
import RoleFormModal from "../components/RoleFormModal";

export default function RolesPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("identity:roles:manage");

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [permissions, setPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(null);

  // null = closed. { role: null } = create, { role: rec } = edit.
  const [formTarget, setFormTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRoles(await listRoles());
    } catch (err) {
      setLoadError(err?.message || "Failed to load roles.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPermissions = useCallback(async () => {
    setPermissionsLoading(true);
    setPermissionsError(null);
    try {
      setPermissions(await listPermissions());
    } catch (err) {
      setPermissionsError(err?.message || "Failed to load the permission catalog.");
    } finally {
      setPermissionsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    loadPermissions();
  }, [refresh, loadPermissions]);

  const handleSubmit = useCallback(
    async (values) => {
      const editing = formTarget?.role;
      const saved = editing
        ? await updateRole(editing.id, values)
        : await createRole(values);
      await refresh();
      return saved;
    },
    [formTarget, refresh]
  );

  const handleDeactivate = useCallback(async () => {
    await deactivateRole(deactivateTarget.id);
    setDeactivateTarget(null);
    await refresh();
  }, [deactivateTarget, refresh]);

  const activeCount = roles.filter((r) => r.isActive).length;
  const grantedCount = new Set(roles.flatMap((r) => r.permissions)).size;

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Role",
        cell: ({ row }) => (
          <div className="min-w-0">
            <span className="block font-medium text-ink">{row.original.name}</span>
            {row.original.description && (
              <span className="block text-xs text-muted mt-0.5">{row.original.description}</span>
            )}
          </div>
        ),
      },
      {
        accessorKey: "level",
        header: "Level",
        width: 160,
        cell: ({ getValue }) => <StatusBadge map="roleLevel" status={getValue()} size="sm" />,
      },
      {
        // Sorts by count rather than by the joined string, which is what you
        // actually want to order by. The old page rendered the full
        // comma-joined list, which for an admin role ran off the screen.
        id: "permissionCount",
        header: "Permissions",
        accessorFn: (row) => row.permissions.length,
        width: 260,
        cell: ({ row }) => {
          const { permissions: granted } = row.original;
          if (granted.length === 0) {
            return <span className="text-xs text-muted italic">None granted</span>;
          }
          return (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full bg-gray-100 text-slate-700 text-xs font-semibold">
                {granted.length}
              </span>
              <span className="text-xs text-muted truncate" title={granted.join(", ")}>
                {granted.slice(0, 2).join(", ")}
                {granted.length > 2 && ` +${granted.length - 2} more`}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: "isActive",
        header: "Status",
        width: 120,
        cell: ({ getValue }) => (
          <StatusBadge map="record" status={getValue() ? "active" : "inactive"} size="sm" />
        ),
      },
      ...(canManage
        ? [
            {
              id: "actions",
              header: "",
              align: "right",
              width: 170,
              sortAllowed: false,
              cell: ({ row }) => (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormTarget({ role: row.original })}
                  >
                    Edit
                  </Button>
                  {row.original.isActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setDeactivateTarget(row.original)}
                    >
                      Deactivate
                    </Button>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canManage]
  );

  return (
    <div>
      <PageHeader
        title="Roles"
        subtitle="A named bundle of permissions, granted to people at one level of the hierarchy."
        actions={
          canManage && (
            <Button variant="primary" onClick={() => setFormTarget({ role: null })}>
              <Plus size={15} /> New role
            </Button>
          )
        }
      />

      {!loading && !loadError && roles.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <StatTile icon={ShieldCheck} label="Total Roles" value={roles.length} />
          <StatTile icon={Layers} label="Active" value={activeCount} />
          <StatTile
            icon={KeyRound}
            label="Permissions Granted"
            value={grantedCount}
            hint={permissions.length ? `of ${permissions.length} in the catalog` : undefined}
          />
        </div>
      )}

      {/* Surfaced on the page, not just inside the modal: the picker can't be
          used at all until the catalog loads, so saying so here saves opening
          the form to find out. */}
      {permissionsError && (
        <Alert variant="danger" title="Permission catalog unavailable" className="mb-4">
          {permissionsError} Roles are listed below, but editing one is disabled until
          this loads.{" "}
          <button type="button" onClick={loadPermissions} className="underline font-semibold">
            Retry
          </button>
        </Alert>
      )}

      {loading ? (
        <SkeletonTable rows={5} columns={canManage ? 5 : 4} />
      ) : loadError ? (
        <EmptyState
          icon={ShieldCheck}
          title="Couldn't load roles"
          description={loadError}
          action={
            <Button variant="secondary" onClick={refresh}>
              Try again
            </Button>
          }
        />
      ) : roles.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No roles yet"
          description="Create a role, attach permissions to it, then grant it to people on the Users page."
          action={
            canManage && (
              <Button variant="secondary" onClick={() => setFormTarget({ role: null })}>
                <Plus size={15} /> New role
              </Button>
            )
          }
        />
      ) : (
        <DataTable table={{ columns, rows: roles }} canSearch />
      )}

      {/* Keyed so switching between create and editing a different row
          re-seeds the form's initial state instead of keeping stale input. */}
      <RoleFormModal
        key={formTarget?.role?.id ?? "new"}
        open={Boolean(formTarget)}
        role={formTarget?.role || null}
        permissions={permissions}
        permissionsLoading={permissionsLoading}
        permissionsError={permissionsError}
        onClose={() => setFormTarget(null)}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        open={Boolean(deactivateTarget)}
        entityLabel="role"
        name={deactivateTarget?.name}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
      >
        Anyone currently holding this role keeps it on record but stops getting the{" "}
        {deactivateTarget?.permissions?.length || 0} permission
        {deactivateTarget?.permissions?.length === 1 ? "" : "s"} it grants. Existing
        sessions keep their current access until the user signs in again.
      </ConfirmModal>
    </div>
  );
}
